import React from 'react';
import { motion } from 'framer-motion';
import { CDIsotype } from './BrandMark';
const ease = [0.22, 1, 0.36, 1] as const;
type ResourceCardProps = {
  kicker: string;
  title: string;
  accent: 'blue' | 'violet' | 'mixed';
  className?: string;
};
const ACCENT: Record<ResourceCardProps['accent'], string> = {
  blue: 'linear-gradient(135deg, #2563FF, #6D8DFF)',
  violet: 'linear-gradient(135deg, #7A4DFF, #B49CFF)',
  mixed: 'linear-gradient(135deg, #2563FF, #8B5CF6)'
};

/** Portada genérica de recurso digital: limpia, editorial, sin temática fija. */
function ResourceCard({
  kicker,
  title,
  accent,
  className = ''
}: ResourceCardProps) {
  return <div className={`overflow-hidden rounded-2xl border border-ink/[0.07] bg-white shadow-card ${className}`} data-id="element-156" data-mp="1ebslcovqefz9">
      <div className="relative aspect-[4/5] p-4" data-id="element-157" data-mp="1nae1kddqdiwv">
        <div aria-hidden="true" className="absolute inset-x-4 top-4 h-[46%] overflow-hidden rounded-xl" style={{
        background: ACCENT[accent]
      }} data-id="element-158" data-mp="1nb7bfn7zkoj8">
          <span className="absolute -right-6 -top-8 h-24 w-24 rounded-full border border-white/40" aria-hidden="true" data-id="element-159" data-mp="26bjxm8hrvk3b" />
          <span className="absolute -bottom-10 -left-6 h-24 w-24 rounded-full bg-white/15" aria-hidden="true" data-id="element-160" data-mp="2adwzumxb8zvc" />
          <CDIsotype className="absolute bottom-3 left-3 h-4 w-4 text-white" data-id="element-161" data-mp="2kbb4zqdsa28c" />
        </div>
        <div className="absolute inset-x-4 bottom-4" data-id="element-162" data-mp="2pa5ybm9dn96p">
          <p className="text-[9px] font-medium uppercase tracking-[0.2em] text-ink-muted" data-id="element-163" data-mp="2p7jrjoub8xpj">
            {kicker}
          </p>
          <p className="mt-1.5 text-[13px] font-semibold leading-snug text-ink" data-id="element-164" data-mp="2y1y1rczzhi1s">
            {title}
          </p>
          <div className="mt-3 space-y-1.5" data-id="element-165" data-mp="33111chx72w2d">
            <span className="block h-[3px] w-[80%] rounded-full bg-ink/[0.08]" data-id="element-166" data-mp="332vl3swqci4v" />
            <span className="block h-[3px] w-[55%] rounded-full bg-ink/[0.06]" data-id="element-167" data-mp="381fay4eg2e2i" />
          </div>
        </div>
      </div>
    </div>;
}
function Float({
  children,
  className = '',
  duration = 9,
  delay = 0,
  distance = 10,
  appearDelay = 0
}: {
  children: React.ReactNode;
  className?: string;
  duration?: number;
  delay?: number;
  distance?: number;
  appearDelay?: number;
}) {
  return <motion.div className={`absolute ${className}`} initial={{
    opacity: 0,
    y: 16,
    scale: 0.97
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} transition={{
    duration: 0.8,
    delay: appearDelay,
    ease
  }} data-id="element-168" data-mp="2x25zksv8rqk2">
      <motion.div animate={{
      y: [0, -distance, 0]
    }} transition={{
      duration,
      delay,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-169" data-mp="36345jgp8q85a">
        {children}
      </motion.div>
    </motion.div>;
}

/**
 * Composición del hero: geometría abstracta azul → violeta inspirada en el
 * isotipo (arcos, círculos y líneas) con portadas de recursos genéricas.
 */
export function HeroComposition() {
  return <div className="relative mx-auto w-full max-w-[560px]" role="img" aria-label="Composición de portadas de recursos digitales con formas geométricas azul y violeta" data-id="element-170" data-mp="1gmcuy85cfqrg">
      <div className="relative aspect-[1/0.94] w-full" data-id="element-171" data-mp="3sc2krigsaz0y">
        {/* geometría de fondo */}
        <div aria-hidden="true" className="absolute inset-0" data-id="element-172" data-mp="1585aflc1s0c">
          <div className="absolute inset-[6%] rounded-full blur-[70px]" style={{
          background: 'radial-gradient(45% 45% at 28% 26%, rgba(37,99,246,0.20), transparent 70%), radial-gradient(45% 45% at 74% 70%, rgba(122,77,255,0.18), transparent 70%)'
        }} data-id="element-173" data-mp="3ij3vrzhdl6gt" />
          <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" preserveAspectRatio="none" data-id="element-174" data-mp="3diplsbw7rmg5">
            <defs data-id="element-175" data-mp="34l6uwqvw2dwc">
              <linearGradient id="heroArc" x1="0" y1="1" x2="1" y2="0" data-id="element-176" data-mp="34knnw57s19x3">
                <stop offset="0%" stopColor="#2563FF" data-id="element-177" data-mp="xtehbnkl3mon" />
                <stop offset="100%" stopColor="#8B5CF6" data-id="element-178" data-mp="xruzvtn6z5la" />
              </linearGradient>
            </defs>
            <g fill="none" stroke="url(#heroArc)" vectorEffect="non-scaling-stroke" data-id="element-179" data-mp="2yvx6dd00lzsl">
              <circle cx="50" cy="50" r="44" strokeWidth="1" opacity="0.22" data-id="element-180" data-mp="2txvnas2la0h4" />
              <circle cx="50" cy="50" r="33" strokeWidth="1" opacity="0.14" data-id="element-181" data-mp="2pupkjueoqrfe" />
              <path d="M8 74 A 46 46 0 0 1 78 12" strokeWidth="6" strokeLinecap="round" opacity="0.16" data-id="element-182" data-mp="2kw4jjsrm03pw" />
              <path d="M92 34 A 46 46 0 0 1 30 92" strokeWidth="2" strokeLinecap="round" opacity="0.35" data-id="element-183" data-mp="2fz4enl6hx70d" />
            </g>
          </svg>
          <div className="absolute right-[6%] top-[8%] h-16 w-16 rounded-full brand-gradient-bg opacity-90" data-id="element-184" data-mp="264vq18kl5y7h" />
          <div className="absolute bottom-[10%] left-[4%] h-10 w-10 rounded-xl border-2 border-electric/30" data-id="element-185" data-mp="1x6a7vej10l78" />
          <div className="dot-grid absolute bottom-[14%] right-[12%] h-20 w-24 opacity-70" data-id="element-186" data-mp="1s6xa3yqm9tpv" />
        </div>

        {/* portada principal */}
        <Float className="left-[24%] top-[10%] w-[44%]" duration={11} distance={8} appearDelay={0.1} data-id="element-187" data-mp="7owihy5teivz">
          <ResourceCard kicker="eBook" title="Recursos para aprender, crear y emprender" accent="mixed" data-id="element-188" data-mp="hnvk4ws5xe9h" />
        </Float>

        {/* portadas secundarias */}
        <Float className="left-[1%] top-[26%] w-[31%]" duration={9.5} delay={0.8} distance={12} appearDelay={0.25} data-id="element-189" data-mp="3l4uhg8km6z5w">
          <div style={{
          transform: 'rotate(-5deg)'
        }} data-id="element-190" data-mp="3q11hjkqbxgfx">
            <ResourceCard kicker="Guía" title="Paso a paso" accent="blue" data-id="element-191" data-mp="3u47jig6eslgl" />
          </div>
        </Float>

        <Float className="right-[0%] top-[38%] w-[31%]" duration={10.5} delay={0.4} distance={12} appearDelay={0.35} data-id="element-192" data-mp="2rwavitbh82r">
          <div style={{
          transform: 'rotate(5deg)'
        }} data-id="element-193" data-mp="36ed5vwe9msva">
            <ResourceCard kicker="Plantillas" title="Listas para editar" accent="violet" data-id="element-194" data-mp="36d25m6k1438x" />
          </div>
        </Float>

        {/* chips de servicio */}
        <Float className="left-[10%] bottom-[6%] w-[40%]" duration={8.5} delay={1.2} distance={10} appearDelay={0.45} data-id="element-195" data-mp="nahov9nc2yv8">
          <div className="flex items-center gap-3 rounded-2xl border border-ink/[0.07] bg-white/90 px-4 py-3 shadow-soft backdrop-blur-md" data-id="element-196" data-mp="ie0p1i6ruw2b">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl brand-gradient-bg" data-id="element-197" data-mp="3lt6hnk8686td">
              <CDIsotype className="h-4 w-4 text-white" data-id="element-198" data-mp="3n0gze9xt46p" />
            </span>
            <div data-id="element-199" data-mp="3vqszaeguuch1">
              <p className="text-[12.5px] font-medium leading-none text-ink" data-id="element-200" data-mp="3vqb4zndr68br">
                Descarga inmediata
              </p>
              <p className="mt-1.5 text-[11px] font-light leading-none text-ink-muted" data-id="element-201" data-mp="1f0cfd9mxgulh">
                Formatos editables
              </p>
            </div>
          </div>
        </Float>

        <Float className="right-[4%] bottom-[12%] w-[27%]" duration={9} delay={0.6} distance={11} appearDelay={0.55} data-id="element-202" data-mp="1ij1ss2mljvpz">
          <div className="rounded-2xl border border-ink/[0.07] bg-white/90 p-3 shadow-soft backdrop-blur-md" data-id="element-203" data-mp="18mgltbr0oaji">
            <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-ink-muted" data-id="element-204" data-mp="1dlt55mioizhc">
              Categorías
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-205" data-mp="21cferi8c0rtg">
              {['Aprender', 'Crear', 'Emprender'].map(c => <span key={c} className="rounded-full border border-ink/[0.08] bg-paper-alt px-2 py-[3px] text-[9px] font-light text-ink-soft" data-id="element-206" data-mp="1rkj3t6l5d77t">
                  {c}
                </span>)}
            </div>
          </div>
        </Float>
      </div>
    </div>;
}